import { LessonController, LessonPhases } from './lesson';
import { QuizOverlay } from './quiz/overlay';
import { showXpPopup, flashCard } from './quiz/feedback';
import { getUiSoundPlayer } from './ui-sounds';
import { loadBank, sampleBank } from './content/bank-loader';
import { loadProgress, saveProgress, createFreshProgress, addFactionXP, skillUnlocked } from '../shared/progress';
import { findOrCreateKP, recordAttempt } from '../shared/student-kp';
import { FACTIONS, factionForSubject } from '../shared/factions';
import { SKILL_CATALOG } from '../shared/skills';
import { mountEscapeButton } from './escape-button';
import { isProMode } from './settings';
import type { LearnDoneCallback } from './app';

export async function runNatureLesson(container: HTMLElement, kpId: string, onDone: LearnDoneCallback): Promise<void> {
  let url: string;
  if (kpId === 'nature.water.states') url = '/data/nature-water-states-南一.json';
  else if (kpId === 'nature.plant.lifecycle') url = '/data/nature-plant-lifecycle-南一.json';
  else if (kpId === 'nature.animal.classify') url = '/data/nature-animals-南一.json';
  else if (kpId === 'nature.sound.light.basic') url = '/data/nature-sound-light-南一.json';
  else if (kpId === 'nature.machine.basic') url = '/data/nature-force-南一.json';
  else if (kpId === 'nature.weather.seasons') url = '/data/nature-weather-南一.json';
  else if (kpId === 'nature.rock.basic') url = '/data/nature-rock-南一.json';
  // ── 三上 backport (grade: 3.1) — Y1.Q3 v1.1 第四批，南一版 ──
  else if (kpId === 'nature.observation.basic.up') url = '/data/nature-observation-up-南一.json';
  else if (kpId === 'nature.water.air.up') url = '/data/nature-water-air-up-南一.json';
  else if (kpId === 'nature.kitchen.science.up') url = '/data/nature-kitchen-up-南一.json';
  else if (kpId === 'nature.light.shadow.up') url = '/data/nature-light-shadow-up-南一.json';
  // ── 三下 新增 7 KP (grade: 3.2) — 三下-NAT-FILL ─────────────────
  else if (kpId === 'nature.plant.growth') url = '/data/nature-plant-growth-南一.json';
  else if (kpId === 'nature.animal.adapt') url = '/data/nature-animal-adapt-南一.json';
  else if (kpId === 'nature.water.temp') url = '/data/nature-water-temp-南一.json';
  else if (kpId === 'nature.weather.measure') url = '/data/nature-weather-measure-南一.json';
  else if (kpId === 'nature.magnet.basic') url = '/data/nature-magnet-南一.json';
  else if (kpId === 'nature.force.deform') url = '/data/nature-force-deform-南一.json';
  else if (kpId === 'nature.light.basic') url = '/data/nature-light-basic-南一.json';
  // ── stage6 wire: meow911 段考真題池(對齊既有 chapter)─────────────
  else if (kpId === 'nature.plant.lifecycle.meow911') url = '/data/meow911-nature-ch1-plant.json';
  else if (kpId === 'nature.animal.classify.meow911') url = '/data/meow911-nature-ch2-animal.json';
  else if (kpId === 'nature.water.states.meow911') url = '/data/meow911-nature-ch3-water.json';
  else if (kpId === 'nature.weather.seasons.meow911') url = '/data/meow911-nature-ch4-weather.json';
  else if (kpId === 'nature.magnet.basic.meow911') url = '/data/meow911-nature-ch5-magnet.json';
  else if (kpId === 'nature.machine.basic.meow911') url = '/data/meow911-nature-ch6-force.json';
  else if (kpId === 'nature.sound.light.basic.meow911') url = '/data/meow911-nature-ch7-sound-light.json';
  else if (kpId === 'nature.rock.basic.meow911') url = '/data/meow911-nature-ch8-rock.json';
  else if (kpId === 'nature.meow911.misc') url = '/data/meow911-nature-misc-pool.json';
  else { throw new Error(`Unknown nature KP: ${kpId}`); }

  const bank = await loadBank(url);
  // Sample 10 of 15 questions, distinct surfaceVariants. Split: 2 warmup + 3 guide + 5 challenge
  const sampled = sampleBank(bank, 10);
  const phases: LessonPhases = {
    warmup: sampled.slice(0, 2),
    guide: sampled.slice(2, 5),
    challenge: sampled.slice(5, 10),
  };

  // Load (or create) progress early so we can record KP attempts during the lesson
  let progress = loadProgress();
  if (!progress) progress = createFreshProgress('指揮官');
  // v24 教學專業模式 — boss directive 2026-05-22: 教學要專業.
  const pro = isProMode(progress);
  const kp = findOrCreateKP(progress, kpId, 'nature');

  const lesson = new LessonController(phases, 2, (correct) => {
    recordAttempt(kp, correct ? 'correct' : 'wrong', new Date().toISOString());
    saveProgress(progress!);
  }); // medium difficulty

  let overlay: QuizOverlay | null = new QuizOverlay({
    container,
    proMode: pro,
    onAnswer: (answer: number | string) => {
      const card = container.querySelector('.card') as HTMLElement | null;
      const result = lesson.submitAnswer(answer);
      if (!pro && card) flashCard(card, result.correct ? 'correct' : 'wrong');
      if (result.correct) {
        if (!pro) getUiSoundPlayer().play('answer-correct', 0.5);
      } else {
        if (!pro) getUiSoundPlayer().play('answer-wrong', 0.4);
      }
      if (!pro) {
        if (result.xp > 0) {
          const faction = FACTIONS[factionForSubject('nature')];
          getUiSoundPlayer().play('xp-popup', 0.35);
          showXpPopup(`+${Math.round(result.xp)} ${faction.emoji}`, '#81c784');
        } else if (!result.correct) {
          showXpPopup('沒關係 +2', '#80cbc4');
        }
      }
      setTimeout(() => {
        if (lesson.state === 'done') finish();
        else renderCurrent();
      }, result.correct ? 500 : 900);
    },
  });

  const unmountEscape = mountEscapeButton(() => {
    overlay?.destroy();
    overlay = null;
    unmountEscape();
    onDone({ newUnlocks: [], exited: true });
  });

  function renderCurrent(): void {
    if (!overlay) return;
    overlay.render(lesson.currentQuestion(), lesson.state, lesson.positionInPhase());
  }

  function finish(): void {
    unmountEscape();
    overlay?.destroy();
    overlay = null;
    // Reuse the progress object already held in closure (has KP updates from recordKP callbacks)
    const faction = factionForSubject('nature');
    const xp = Math.round(lesson.totalXP);
    addFactionXP(progress!, faction, xp);
    // Generic unlock detection
    const newUnlocks: string[] = [];
    for (const skill of SKILL_CATALOG) {
      if (skill.faction === 'builtin') continue;
      if (skillUnlocked(progress!, skill.id)) continue;
      if (progress!.factionXP[skill.faction] >= skill.cost) {
        progress!.unlockedSkills.push(skill.id);
        newUnlocks.push(skill.id);
      }
    }
    saveProgress(progress!);
    onDone({ newUnlocks });
  }

  renderCurrent();
}
